'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from 'next-themes';
import { CaretLeft, X, ArrowDown } from '@phosphor-icons/react';
import {
  ChatDotsIcon,
  FaceDelightIcon,
  FaceHappyIcon,
  FaceNeutralIcon,
  FaceSadIcon,
  FaceSmileIcon,
  SendIcon,
  SpinnerIcon,
  StarIcon,
  UsersIcon,
} from '@/components/icons';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';
import { useRouter } from 'next/navigation';
import { ymReviewSubmitted, tmrReviewSubmitted } from '@/lib/analytics';

/* ─── Types ─── */
interface ReviewData {
  id: number;
  rating: number;
  review_text: string;
  userName: string;
  created_at: string;
}

interface ReviewsResponse {
  reviews: ReviewData[];
  averageRating: number;
  totalReviews: number;
  currentPage: number;
  totalPages: number;
}

/* ─── Rating config ─── */
const RATINGS = [
  { value: 1, Icon: FaceSadIcon, label: 'Ужасно' },
  { value: 2, Icon: FaceNeutralIcon, label: 'Плохо' },
  { value: 3, Icon: FaceSmileIcon, label: 'Нормально' },
  { value: 4, Icon: FaceHappyIcon, label: 'Хорошо' },
  { value: 5, Icon: FaceDelightIcon, label: 'Отлично' },
];

/* ─── Avatar colors ─── */
const AVATAR_COLORS = [
  'bg-rose-500', 'bg-amber-500', 'bg-emerald-500', 'bg-sky-500',
  'bg-violet-500', 'bg-pink-500', 'bg-teal-500', 'bg-orange-500',
  'bg-indigo-500', 'bg-cyan-500', 'bg-lime-500', 'bg-fuchsia-500',
];

function getAvatarColor(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name: string) {
  return name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) || '?';
}

/* ─── Format helpers ─── */
function formatTime(dateStr: string) {
  return new Date(dateStr).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

function formatDateLabel(dateStr: string) {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return 'Сегодня';
  if (days === 1) return 'Вчера';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

/* ─── Stars ─── */
function Stars({ rating }: { rating: number }) {
  return (
    <span className="inline-flex gap-px">
      {[1, 2, 3, 4, 5].map((i) => (
        <StarIcon
          key={i}
          weight={i <= rating ? 'fill' : 'regular'}
          className={cn('w-3.5 h-3.5', i <= rating ? 'text-amber-400' : 'text-[var(--text-muted)]/40')}
        />
      ))}
    </span>
  );
}

/* ─── Date separator (like in messengers) ─── */
function DateSeparator({ label }: { label: string }) {
  return (
    <div className="flex justify-center my-4">
      <span className={cn(
        'px-3 py-1 rounded-pill t-micro font-medium',
        'bg-[var(--surface-secondary)] text-[var(--text-muted)] border border-[var(--border-default)]',
      )}>
        {label}
      </span>
    </div>
  );
}

/* ─── Chat bubble (message style) ─── */
function ChatBubble({ review, showAvatar, isDark }: { review: ReviewData; showAvatar: boolean; isDark: boolean }) {
  const color = getAvatarColor(review.userName);
  const initials = getInitials(review.userName);
  const r = RATINGS.find((x) => x.value === review.rating) ?? RATINGS[2];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="flex items-end gap-2 px-3 sm:px-4"
    >
      {/* Avatar */}
      <div className="w-8 h-8 flex-shrink-0">
        {showAvatar ? (
          <div className={cn('w-8 h-8 rounded-pill flex items-center justify-center text-white text-xs font-bold', color)}>
            {initials}
          </div>
        ) : (
          <div className="w-8" />
        )}
      </div>

      {/* Bubble */}
      <div className="max-w-[85%] sm:max-w-[70%]">
        {/* Name + rating row */}
        {showAvatar && (
          <div className="flex items-center gap-2 mb-0.5 pl-1">
            <span className="text-xs font-semibold text-[var(--text-primary)]">
              {review.userName}
            </span>
            <Stars rating={review.rating} />
            <r.Icon weight="fill" className="w-3.5 h-3.5 text-amber-400" />
          </div>
        )}

        {/* ВНИМАНИЕ, видимое изменение. До миграции у пузыря был «хвостик»:
            все углы 20, а нижний левый — 8. После перехода на четыре ступени
            восьмёрка ушла в card = 20, нижний левый сравнялся с остальными,
            и пузырь стал ровным прямоугольником без хвостика. Это прямое
            следствие ответа 32 (четыре ступени, пятую не заводим) — отдельной
            ступени под «хвостик» в системе нет. Если хвостик нужен вернуть,
            это решение владельца, а не миграции.

            Заодно убрано условие вида «showAvatar ? A : B» на нижний левый
            угол: обе ветки задавали одно и то же значение (в исходнике тоже —
            там в обеих ветках стоял один и тот же класс), а базовое скругление
            карточки этот угол уже красит. Условие только притворялось, что
            форма пузыря зависит от showAvatar. Ни одного пикселя не изменилось.

            Имена классов в этом комментарии намеренно не написаны целиком:
            сканер Tailwind читает и комментарии, и вшил бы в сборку утилиту,
            которой в разметке больше нет. */}
        <div className={cn(
          'relative rounded-card px-3.5 py-2.5',
          isDark
            ? 'bg-[var(--surface)] border border-[var(--border-default)]'
            : 'bg-white border border-gray-200',
        )}>
          <p className="text-sm text-[var(--text-primary)] leading-relaxed whitespace-pre-line">
            {review.review_text}
          </p>
          <div className="flex justify-end mt-1">
            <span className="t-nano text-[var(--text-muted)]">
              {formatTime(review.created_at)}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ─── Review Form Modal ─── */
function ReviewFormModal({
  open,
  onClose,
  onSubmitted,
}: {
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const [name, setName] = useState('');
  const [rating, setRating] = useState<number | null>(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Введите имя';
    if (!rating) e.rating = 'Выберите оценку';
    if (!text.trim()) e.text = 'Напишите отзыв';
    else if (text.trim().length < 10) e.text = 'Минимум 10 символов';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSending(true);
    try {
      await apiClient.post('/reviews', {
        rating,
        reviewText: text.trim(),
        guestName: name.trim(),
      });
      onClose();
      onSubmitted();
      ymReviewSubmitted(rating!);
      tmrReviewSubmitted();
      setName(''); setRating(null); setText(''); setErrors({});
    } catch {
      setErrors({ submit: 'Ошибка отправки. Попробуйте позже.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 60 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              'w-full sm:max-w-md rounded-t-card sm:rounded-card border overflow-hidden',
              'bg-[var(--surface)] border-[var(--border-default)]',
            )}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--border-default)]">
              <h2 className="text-base font-semibold text-[var(--text-primary)]">Новое сообщение</h2>
              <button onClick={onClose} className="p-1.5 rounded-card hover:bg-[var(--surface-hover)] transition-colors">
                <X className="w-5 h-5 text-[var(--text-secondary)]" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Ваше имя</label>
                <input
                  value={name}
                  onChange={(e) => { setName(e.target.value); if (errors.name) setErrors((p) => ({ ...p, name: '' })); }}
                  placeholder="Как вас зовут?"
                  className={cn(
                    'w-full px-3.5 py-2 rounded-card text-sm border outline-none transition-colors',
                    'bg-[var(--surface-secondary)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]',
                    errors.name ? 'border-red-500' : 'border-[var(--border-default)] focus:border-[var(--primary)]',
                  )}
                />
                {errors.name && <p className="t-micro text-red-500 mt-0.5">{errors.name}</p>}
              </div>

              {/* Rating */}
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-2">Оценка</label>
                <div className="flex justify-center gap-1.5">
                  {RATINGS.map((r) => {
                    const active = rating === r.value;
                    return (
                      <button
                        key={r.value}
                        type="button"
                        onClick={() => { setRating(r.value); if (errors.rating) setErrors((p) => ({ ...p, rating: '' })); }}
                        className={cn(
                          'flex flex-col items-center gap-0.5 px-3 py-2 rounded-card border-2 transition-all',
                          active
                            ? 'border-[var(--primary)] bg-[var(--primary-subtle)] scale-105'
                            : 'border-[var(--border-default)] hover:border-[var(--primary)]/40',
                        )}
                      >
                        <r.Icon
                          weight={active ? 'fill' : 'regular'}
                          className={cn('w-6 h-6', active ? 'text-[var(--primary)]' : 'text-[var(--text-muted)]')}
                        />
                        <span className="t-nano font-medium text-[var(--text-muted)]">{r.label}</span>
                      </button>
                    );
                  })}
                </div>
                {errors.rating && <p className="t-micro text-red-500 mt-1 text-center">{errors.rating}</p>}
              </div>

              {/* Text + send */}
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Сообщение</label>
                <div className={cn(
                  'flex items-end gap-2 rounded-card border p-2',
                  'bg-[var(--surface-secondary)]',
                  errors.text ? 'border-red-500' : 'border-[var(--border-default)] focus-within:border-[var(--primary)]',
                )}>
                  <textarea
                    value={text}
                    onChange={(e) => { setText(e.target.value); if (errors.text) setErrors((p) => ({ ...p, text: '' })); }}
                    rows={3}
                    placeholder="Поделитесь впечатлениями..."
                    className="flex-1 bg-transparent text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] outline-none resize-none"
                  />
                  <button
                    onClick={handleSubmit}
                    disabled={sending}
                    className={cn(
                      'p-2 rounded-card transition-all flex-shrink-0',
                      'bg-[var(--primary)] text-white hover:opacity-90 disabled:opacity-50',
                    )}
                  >
                    {sending ? (
                      <SpinnerIcon className="w-5 h-5 animate-spin" />
                    ) : (
                      <SendIcon className="w-5 h-5" weight="fill" />
                    )}
                  </button>
                </div>
                {errors.text && <p className="t-micro text-red-500 mt-0.5">{errors.text}</p>}
                {errors.submit && <p className="t-micro text-red-500 mt-0.5">{errors.submit}</p>}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ─── Main Page ─── */
export function ReviewsContent() {
  const [mounted, setMounted] = useState(false);
  const { resolvedTheme } = useTheme();
  const isDark = mounted ? resolvedTheme === 'dark' : false;
  const router = useRouter();

  const [reviews, setReviews] = useState<ReviewData[]>([]);
  const [avg, setAvg] = useState(0);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setMounted(true); }, []);

  const fetchReviews = useCallback(async (p = 1, append = false) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const data = await apiClient.get<ReviewsResponse>('/reviews', { page: p, limit: 15 });
      setReviews((prev) => append ? [...prev, ...data.reviews] : data.reviews);
      setAvg(data.averageRating);
      setTotal(data.totalReviews);
      setPage(data.currentPage);
      setTotalPages(data.totalPages);
    } catch {
      if (!append) setReviews([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const loadMore = () => {
    if (page < totalPages && !loadingMore) fetchReviews(page + 1, true);
  };

  /* Group reviews by date for separators */
  const groupedMessages: { type: 'date'; label: string }[] | { type: 'review'; review: ReviewData; showAvatar: boolean }[] = [];
  let lastDate = '';
  let lastName = '';

  const sortedReviews = [...reviews].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );

  for (const review of sortedReviews) {
    const dateLabel = formatDateLabel(review.created_at);
    if (dateLabel !== lastDate) {
      (groupedMessages as any[]).push({ type: 'date', label: dateLabel });
      lastDate = dateLabel;
      lastName = '';
    }
    const showAvatar = review.userName !== lastName;
    (groupedMessages as any[]).push({ type: 'review', review, showAvatar });
    lastName = review.userName;
  }

  const AvgIcon = RATINGS.find((r) => r.value === Math.round(avg))?.Icon ?? FaceHappyIcon;

  return (
    <div className={cn(
      'min-h-screen flex flex-col',
      isDark ? 'bg-[var(--bg-primary)]' : 'bg-[var(--surface-secondary)]',
    )}>
      {/* ── Messenger-style header ── */}
      <div className={cn(
        'sticky top-0 z-30 border-b backdrop-blur-md',
        isDark
          ? 'bg-[var(--surface)]/95 border-[var(--border-default)]'
          : 'bg-white/95 border-[var(--border-default)]',
      )}>
        <div className="max-w-3xl mx-auto px-3 sm:px-4 h-14 sm:h-16 flex items-center gap-3">
          {/* Back */}
          <button
            onClick={() => router.back()}
            className="p-1.5 -ml-1 rounded-card hover:bg-[var(--surface-hover)] transition-colors flex-shrink-0"
          >
            <CaretLeft className="w-5 h-5 text-[var(--text-secondary)]" weight="bold" />
          </button>

          {/* Group avatar */}
          <div className="w-10 h-10 rounded-pill bg-[var(--primary)] flex items-center justify-center flex-shrink-0">
            <ChatDotsIcon className="w-5 h-5 text-white" weight="fill" />
          </div>

          {/* Group info */}
          <div className="flex-1 min-w-0">
            <h1 className="text-sm sm:text-base font-semibold text-[var(--text-primary)] truncate">
              Отзывы Азиаточка
            </h1>
            <div className="flex items-center gap-1.5">
              <UsersIcon className="w-3 h-3 text-[var(--text-muted)]" />
              <span className="t-micro text-[var(--text-muted)]">
                {total} отзывов ·{' '}
                <AvgIcon
                  weight="fill"
                  className="inline-block w-3 h-3 align-[-0.125em] text-amber-400"
                />{' '}
                {avg.toFixed(1)}
              </span>
            </div>
          </div>

          {/* Write button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setFormOpen(true)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-pill text-xs font-medium transition-colors',
              'bg-[var(--primary)] text-white',
            )}
          >
            <SendIcon className="w-3.5 h-3.5" weight="fill" />
            <span className="hidden sm:inline">Написать</span>
          </motion.button>
        </div>
      </div>

      {/* ── Chat area ── */}
      <div ref={chatContainerRef} className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto py-4">
          {/* Breadcrumbs */}
          <nav aria-label="Хлебные крошки" className="px-3 sm:px-4 pb-3">
            <ol className="flex flex-wrap items-center gap-1.5 text-xs sm:text-sm">
              <li className="flex items-center gap-1.5">
                <Link href="/" className="text-[var(--text-secondary)] hover:text-[var(--primary)] transition-colors duration-150 whitespace-nowrap">Главная</Link>
              </li>
              <li className="flex items-center gap-1.5">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-[var(--text-tertiary)] shrink-0" aria-hidden="true">
                  <path d="M6 3.5L10.5 8L6 12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="text-[var(--text-tertiary)]">Отзывы</span>
              </li>
            </ol>
          </nav>

          {/* Pinned info message */}
          <div className="flex justify-center mb-2">
            <div className={cn(
              'px-4 py-2 rounded-card text-center max-w-sm',
              'bg-[var(--primary-subtle)] border border-[var(--primary)]/15',
            )}>
              <p className="text-xs text-[var(--text-secondary)]">
                💬 Здесь собраны реальные отзывы наших клиентов. Поделитесь и вы своим мнением!
              </p>
            </div>
          </div>

          {/* Load more (older) */}
          {page < totalPages && !loading && (
            <div className="flex justify-center my-3">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className={cn(
                  'flex items-center gap-1.5 px-4 py-1.5 rounded-pill text-xs font-medium transition-colors',
                  'bg-[var(--surface)] border border-[var(--border-default)] text-[var(--text-secondary)]',
                  'hover:bg-[var(--surface-hover)]',
                )}
              >
                {loadingMore ? (
                  <SpinnerIcon className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <ArrowDown className="w-3.5 h-3.5 rotate-180" />
                )}
                Загрузить ещё
              </button>
            </div>
          )}

          {/* Loading state */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <SpinnerIcon className="w-7 h-7 animate-spin text-[var(--primary)]" />
            </div>
          ) : reviews.length === 0 ? (
            /* Empty state */
            <div className="text-center py-16 px-4">
              <div className="w-16 h-16 rounded-pill bg-[var(--surface)] border border-[var(--border-default)] flex items-center justify-center mx-auto mb-4">
                <ChatDotsIcon className="w-8 h-8 text-[var(--text-muted)]" />
              </div>
              <h3 className="text-base font-semibold text-[var(--text-primary)] mb-1">
                Пока тихо...
              </h3>
              <p className="text-sm text-[var(--text-secondary)] mb-5">
                Станьте первым, кто напишет отзыв
              </p>
              <button
                onClick={() => setFormOpen(true)}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-pill bg-[var(--primary)] text-white text-sm font-medium"
              >
                <SendIcon className="w-4 h-4" weight="fill" />
                Написать первый отзыв
              </button>
            </div>
          ) : (
            /* Messages */
            <div className="space-y-1.5">
              {(groupedMessages as any[]).map((item, i) =>
                item.type === 'date' ? (
                  <DateSeparator key={`date-${i}`} label={item.label} />
                ) : (
                  <ChatBubble
                    key={item.review.id}
                    review={item.review}
                    showAvatar={item.showAvatar}
                    isDark={isDark}
                  />
                ),
              )}
            </div>
          )}

          <div ref={chatEndRef} />
        </div>
      </div>

      {/* ── Bottom input bar (visual, opens modal) ── */}
      <div className={cn(
        'sticky bottom-0 z-20 border-t backdrop-blur-md',
        isDark
          ? 'bg-[var(--surface)]/95 border-[var(--border-default)]'
          : 'bg-white/95 border-[var(--border-default)]',
      )}>
        <div className="max-w-3xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3">
          <button
            onClick={() => setFormOpen(true)}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-2.5 rounded-card text-left transition-colors',
              'bg-[var(--surface-secondary)] border border-[var(--border-default)]',
              'hover:bg-[var(--surface-hover)]',
            )}
          >
            <span className="text-sm text-[var(--text-muted)] flex-1">Написать отзыв...</span>
            <div className="p-1.5 rounded-card bg-[var(--primary)]">
              <SendIcon className="w-4 h-4 text-white" weight="fill" />
            </div>
          </button>
        </div>
      </div>

      {/* ── Form modal ── */}
      <ReviewFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmitted={() => fetchReviews(1)}
      />
    </div>
  );
}
